// src/pages/MedAIDemo.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FaPaperPlane, FaHospital, FaClinicMedical, FaPaperclip, FaTimes } from 'react-icons/fa';
import './MedAIDemo.css';

const MedAIDemo = () => {
  const [mode, setMode] = useState('clinic'); // 'clinic' | 'pharmacy'
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Set initial welcome message based on mode
  useEffect(() => {
    const welcomeText = mode === 'clinic' 
      ? 'أهلاً بك في نظام Codical MedAI للعيادات 🏥\nكيف يمكنني مساعدتك اليوم؟ (حجز موعد، استعلام عن أسعار)'
      : 'أهلاً بك في نظام Codical MedAI للصيدليات 💊\nأرسل لنا الروشتة أو اسم الدواء وسنقوم بتوصيله إليك فوراً.';
      
    setMessages([
      {
        role: 'bot',
        content: welcomeText,
        time: new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  }, [mode]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, imagePreview]);

  const handleModeChange = (e) => {
    setMode(e.target.value);
    setMessages([]);
    setImagePreview(null);
    setInputMessage('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setImagePreview(null);
    if(fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() && !imagePreview) return;

    const userText = inputMessage.trim();
    const imageToSend = imagePreview;
    const currentTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });

    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userText, 
      image: imageToSend,
      time: currentTime 
    }]);
    
    setInputMessage('');
    clearImage();
    setIsTyping(true);

    try {
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/medai_chat.php' : '/api/medai_chat.php';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: userText, 
          image: imageToSend,
          mode: mode,
          history: messages,
          session_name: "Demo User"
        })
      });
      
      const data = await response.json();
      
      const replyTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
      
      if (data.status === 'success') {
        setMessages(prev => [...prev, { role: 'bot', content: data.reply, time: replyTime }]);
      } else {
        setMessages(prev => [...prev, { role: 'bot', content: "عذراً، حدث خطأ في النظام.", time: replyTime }]);
      }
    } catch (error) {
      const replyTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
      setMessages(prev => [...prev, { role: 'bot', content: "عذراً، الخادم مسؤل الذكاء الاصطناعي لا يستجيب.", time: replyTime }]);
    }
    
    setIsTyping(false);
  };

  return (
    <div className="medai-demo-container">
      <div className="whatsapp-box">
        {/* Header */}
        <div className="wa-header">
          <div className="wa-header-left">
            <div className="wa-avatar">
              {mode === 'clinic' ? <FaHospital /> : <FaClinicMedical />}
            </div>
            <div className="wa-info">
              <span className="wa-name">
                {mode === 'clinic' ? 'بوت العيادات' : 'بوت الصيدلية'}
              </span>
              <span className="wa-status">{isTyping ? 'يكتب...' : 'متصل الآن'}</span>
            </div>
          </div>
          <div className="wa-mode-switcher">
            <select value={mode} onChange={handleModeChange}>
              <option value="clinic">🏥 نمط العيادات</option>
              <option value="pharmacy">💊 نمط الصيدلية</option>
            </select>
          </div>
        </div>

        {/* Chat Area */}
        <div className="wa-chat-area">
          {messages.map((msg, index) => (
            <div key={index} className={`wa-message ${msg.role}`}>
              {msg.image && (
                <img src={msg.image} alt="attachment" className="wa-message-image" />
              )}
              {msg.content && <span>{msg.content}</span>}
              <span className="wa-time">{msg.time}</span>
              <div style={{ clear: 'both' }}></div>
            </div>
          ))}
          {isTyping && (
            <div className="wa-typing">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Image Preview Bar */}
        {imagePreview && (
          <div className="wa-image-preview">
            <img src={imagePreview} alt="preview" />
            <span onClick={clearImage}><FaTimes /> إزالة الصورة</span>
          </div>
        )}

        {/* Footer */}
        <form className="wa-footer" onSubmit={handleSendMessage}>
          
          <button type="button" className="wa-attach-btn" onClick={() => fileInputRef.current?.click()} disabled={isTyping}>
            <FaPaperclip />
          </button>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleImageUpload} 
            accept="image/*" 
            style={{ display: 'none' }} 
          />

          <div className="wa-input-container">
            <input
              type="text"
              className="wa-input"
              placeholder="اكتب رسالة..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={isTyping}
            />
          </div>
          <button type="submit" className="wa-send-btn" disabled={(!inputMessage.trim() && !imagePreview) || isTyping}>
            <FaPaperPlane />
          </button>
        </form>
      </div>
    </div>
  );
};

export default MedAIDemo;
